import React, { useState } from 'react';
import { NavLink, Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useRecruitmentData } from '../context/RecruitmentContext';
import { useServicesData } from '../context/ServicesContext';
import { useMetiersData } from '../context/MetiersContext';
import { useSocieteData } from '../context/SocieteContext';
import { useReferencesData } from '../context/ReferencesContext';
import { useUsersData } from '../context/UsersContext';
import { useHeroSlidesData } from '../context/HeroSlidesContext';
import { useEffectsData } from '../context/EffectsContext';
import { useMessagesData } from '../context/MessagesContext';
import { useTechnologiesData } from '../context/TechnologiesContext';
import { useHighlightsData } from '../context/HighlightsContext';
import { useHomeData } from '../context/HomeContext';
import { useContactData } from '../context/ContactContext';
import pb from '../lib/pocketbase';

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // --- ADMIN SPLASH SCREEN LOGIC ---
  const { loading: loadingRecruitment } = useRecruitmentData();
  const { loading: loadingServices } = useServicesData();
  const { loading: loadingMetiers } = useMetiersData();
  const { loading: loadingSociete } = useSocieteData();
  const { loading: loadingReferences } = useReferencesData();
  const { loading: loadingUsers } = useUsersData();
  const { loading: loadingHeroSlides } = useHeroSlidesData();
  const { loading: loadingEffects } = useEffectsData();
  const { loading: loadingMessages, messages } = useMessagesData();
  const { loading: loadingTechnologies } = useTechnologiesData();
  const { loading: loadingHighlights } = useHighlightsData();
  const { loading: loadingHome } = useHomeData();
  const { loading: loadingContact } = useContactData();

  const unreadCount = (messages || []).filter(m => !m.read).length;

  const isLoading = loadingRecruitment || loadingServices || loadingMetiers || loadingSociete || loadingReferences || loadingUsers || loadingHeroSlides || loadingEffects || loadingMessages || loadingTechnologies || loadingHighlights || loadingHome || loadingContact;

  if (isLoading) {
    return (
      <div className="recrutement-loader-container" style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'var(--admin-bg)' }}>
        <div className="loader-visual">
          <div className="loader-orbit orbit-1"></div>
          <div className="loader-orbit orbit-2"></div>
          <div className="loader-orbit orbit-3"></div>
          <div className="loader-logo-wrapper">
            <img src="/images/logo.png" alt="Version Net" className="loader-logo" />
          </div>
        </div>
        <div className="loader-message-wrapper">
          <p className="loader-text" style={{ color: 'var(--admin-text)' }}>Chargement de l'administration...</p>
          <div className="loader-progress-bar">
            <div className="loader-progress-fill"></div>
          </div>
        </div>
      </div>
    );
  }
  // ---------------------------------

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = (nameOrEmail) => {
    if (!nameOrEmail) return 'U';
    const str = String(nameOrEmail);
    const parts = str.includes(' ')
      ? str.split(' ').filter(Boolean)
      : str.split('@')[0].split('.').filter(Boolean);
    const first = parts[0]?.[0] || str[0];
    const second = parts[1]?.[0] || '';
    return (first + second).toUpperCase();
  };

  const avatarUrl = user?.avatar ? pb.files.getUrl(user, user.avatar, { thumb: '64x64' }) : null;

  const getPageTitle = (path) => {
    if (path === '/admin') return 'Tableau de bord';
    if (path.includes('/admin/users')) return 'Utilisateurs';
    if (path.includes('/admin/references')) return 'Références';
    if (path.includes('/admin/hero-slides')) return 'Hero Slides';
    if (path.includes('/admin/effets')) return 'Effets Visuels';
    if (path.includes('/admin/metiers')) return 'Métiers';
    if (path.includes('/admin/societe')) return 'Société';
    if (path.includes('/admin/recrutement')) return 'Recrutement';
    if (path.includes('/admin/services')) return 'Services';
    if (path.includes('/admin/messages')) return 'Messages';
    if (path.includes('/admin/technologies')) return 'Technologies';
    if (path.includes('/admin/highlights')) return 'Annonces';
    if (path.includes('/admin/home')) return 'Contenu Accueil';
    if (path.includes('/admin/contact')) return 'Contenu Contact';
    if (path.includes('/admin/legal')) return 'Mentions Légales';
    return 'Admin';
  };

  return (
    <div className={`admin-layout ${open ? 'admin-nav-open' : ''}`}>
      <header className="admin-header">
        <div className="admin-header-left">
          <button className="admin-burger" aria-label="Ouvrir/fermer le menu" onClick={() => setOpen((v) => !v)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
          </button>
          <div className="admin-breadcrumb">
            <span className="breadcrumb-item">Admin</span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-item active">{getPageTitle(location.pathname)}</span>
          </div>
        </div>

        <div className="admin-header-center">
          <div className="admin-search-bar">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            <input type="text" placeholder="Recherche rapide..." readOnly />
          </div>
        </div>

        <div className="admin-header-right">
          <Link to="/" className="btn-site-link" title="Voir le site">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
            <span>Voir le site</span>
          </Link>

          <div className="admin-header-divider"></div>

          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label="Changer de thème"
            type="button"
            style={{ marginRight: '0.5rem' }}
          >
            <span className="theme-toggle-track">
              <span className="theme-toggle-icon" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l-1.4-1.4M20.4 20.4 19 19M5 19l-1.4 1.4M20.4 3.6 19 5" />
                </svg>
              </span>
              <span className="theme-toggle-icon" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              </span>
              <span
                className={`theme-toggle-thumb ${theme === 'dark' ? 'theme-toggle-thumb-left' : 'theme-toggle-thumb-right'
                  }`}
              />
            </span>
          </button>

          <div style={{ position: 'relative' }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              style={{ padding: '0.25rem', borderRadius: '999px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <div className="avatar-circle" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>
                {avatarUrl ? (
                  <img className="avatar-img" src={avatarUrl} alt="Avatar" />
                ) : (
                  <span className="avatar-initials">{getInitials(user?.name || user?.email)}</span>
                )}
              </div>
            </button>

            {userMenuOpen && (
              <div className="admin-dropdown-menu">
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>{user?.name || 'Administrateur'}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{user?.email}</div>
                </div>
                <button onClick={handleLogout} className="admin-dropdown-item" style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '0.75rem 1rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                  Déconnexion
                </button>
              </div>
            )}
          </div>

          {userMenuOpen && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setUserMenuOpen(false)} />
          )}
        </div>
      </header>
      <aside className="admin-sidebar" onClick={() => setOpen(false)}>
        <div className="admin-sidebar-header">
          <img src="/images/logo.png" alt="Version Net" className="admin-sidebar-logo" />
        </div>
        <nav className="admin-nav">
          <NavLink end to="/admin" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
            <span>Tableau de bord</span>
          </NavLink>

          {/* PAGES DU SITE */}
          <div className="admin-nav-label" style={{ padding: '0.75rem 1rem 0.25rem', fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Contenu du Site</div>

          <NavLink to="/admin/home" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
            <span>Accueil</span>
          </NavLink>
          <NavLink to="/admin/services" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
            <span>Services</span>
          </NavLink>
          <NavLink to="/admin/metiers" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 7h-9"></path><path d="M14 17H5"></path><circle cx="17" cy="17" r="3"></circle><circle cx="7" cy="7" r="3"></circle></svg>
            <span>Métiers</span>
          </NavLink>
          <NavLink to="/admin/recrutement" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><polyline points="16 11 18 13 22 9"></polyline></svg>
            <span>Recrutement</span>
          </NavLink>
          <NavLink to="/admin/references" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
            <span>Références</span>
          </NavLink>
          <NavLink to="/admin/societe" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
            <span>Société</span>
          </NavLink>
          <NavLink to="/admin/contact" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
            <span>Contact</span>
          </NavLink>

          {/* OUTILS */}
          <div className="admin-nav-label" style={{ padding: '1rem 1rem 0.25rem', fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Outils & Administration</div>

          <NavLink to="/admin/messages" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
            <span>Messages</span>
            {unreadCount > 0 && <span className="nav-badge">{unreadCount}</span>}
          </NavLink>
          <NavLink to="/admin/users" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
            <span>Utilisateurs</span>
          </NavLink>
          <NavLink to="/admin/highlights" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
            <span>Annonces</span>
          </NavLink>
          <NavLink to="/admin/technologies" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect><rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect><line x1="6" y1="6" x2="6.01" y2="6"></line><line x1="6" y1="18" x2="6.01" y2="18"></line></svg>
            <span>Technologies</span>
          </NavLink>
          <NavLink to="/admin/effets" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"></path></svg>
            <span>Effets Visuels</span>
          </NavLink>
          <NavLink to="/admin/hero-slides" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
            <span>Hero slides</span>
          </NavLink>
          <NavLink to="/admin/legal" className={({ isActive }) => `admin-link ${isActive ? 'active' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
            <span>Mentions Légales</span>
          </NavLink>
        </nav>
      </aside>
      <main className="admin-content">
        <Outlet />
      </main>
    </div>
  );
}
